"use client";

import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Building,
  User,
  Mail,
  FolderGit,
  Landmark,
  Euro,
  Receipt,
  CheckCircle2,
  AlertTriangle,
  Clock,
} from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { useBreadcrumbStore } from "@/components/layout/breadcrumb";

interface ProjectItem {
  id: string;
  title: string;
  budget: string;
  revenue: string;
  status: "on_track" | "watch" | "at_risk" | "blocked";
  ownerName?: string;
  createdAt: string;
}

interface InvoiceItem {
  id: string;
  amount: string;
  dueDate: string;
  status: "draft" | "sent" | "paid" | "overdue" | "contested";
  createdAt?: string;
}

interface ClientItem {
  id: string;
  name: string;
  industry: string;
  contactName: string;
  contactEmail: string;
  logoText: string;
  projectsCount: number;
  totalRevenue: number;
}

interface ClientProfileClientProps {
  client: ClientItem;
  projects: ProjectItem[];
  invoices: InvoiceItem[];
}

export function ClientProfileClient({
  client,
  projects,
  invoices,
}: ClientProfileClientProps) {
  const setLabel = useBreadcrumbStore((state) => state.setLabel);
  React.useEffect(() => {
    if (client?.name) {
      setLabel(client.id, client.name);
    }
  }, [client?.id, client?.name, setLabel]);

  const [activeTab, setActiveTab] = useState<"projects" | "financials">(
    "projects",
  );

  const formatCurrency = (val: string | number) => {
    const num = typeof val === "string" ? parseFloat(val) : val;
    if (isNaN(num)) return "€0";
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(num);
  };

  const getProjectStatusBadge = (status: ProjectItem["status"]) => {
    switch (status) {
      case "on_track":
        return <Badge variant="success">In Corso (OK)</Badge>;
      case "watch":
        return <Badge variant="warning">In Osservazione</Badge>;
      case "at_risk":
        return <Badge variant="error">A Rischio</Badge>;
      case "blocked":
        return <Badge variant="error">Bloccato</Badge>;
      default:
        return <Badge variant="neutral">{status}</Badge>;
    }
  };

  const getInvoiceStatusBadge = (status: InvoiceItem["status"]) => {
    switch (status) {
      case "paid":
        return <Badge variant="success">Pagato</Badge>;
      case "sent":
        return <Badge variant="info">Inviato</Badge>;
      case "draft":
        return <Badge variant="neutral">Bozza</Badge>;
      case "overdue":
        return <Badge variant="error">Scaduto</Badge>;
      case "contested":
        return <Badge variant="warning">Contestato</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  // Financial aggregates
  const totalBudget = projects.reduce(
    (sum, p) => sum + parseFloat(p.budget || "0"),
    0,
  );
  const totalRevenue = projects.reduce(
    (sum, p) => sum + parseFloat(p.revenue || "0"),
    0,
  );

  const totalInvoiced = invoices.reduce(
    (sum, inv) => sum + parseFloat(inv.amount || "0"),
    0,
  );
  const totalPaid = invoices
    .filter((inv) => inv.status === "paid")
    .reduce((sum, inv) => sum + parseFloat(inv.amount || "0"), 0);
  const totalOutstanding = invoices
    .filter((inv) => inv.status === "sent" || inv.status === "overdue")
    .reduce((sum, inv) => sum + parseFloat(inv.amount || "0"), 0);

  return (
    <div className="space-y-6">
      {/* Back Link */}
      <div>
        <Link
          href="/clients"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Torna all'Anagrafica Clienti
        </Link>
      </div>

      {/* Profile Card Summary & Aggregates */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="lg:col-span-1 flex flex-col justify-between">
          <CardHeader className="pb-4">
            <div className="flex gap-4 items-center border-b border-border/40 pb-4">
              <div className="h-16 w-16 rounded-2xl bg-primary-600 text-text-on-primary flex items-center justify-center font-bold text-2xl shadow-md select-none">
                {client.logoText}
              </div>
              <div className="space-y-1">
                <Badge variant="neutral">{client.industry}</Badge>
                <CardTitle className="text-lg font-bold">
                  {client.name}
                </CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 py-2">
            <div className="space-y-3 text-xs">
              <div className="flex items-center justify-between py-1.5 border-b border-border/30">
                <span className="text-text-secondary font-medium flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5 text-primary-500" />
                  Referente:
                </span>
                <span className="font-semibold text-text-primary">
                  {client.contactName}
                </span>
              </div>
              <div className="flex items-center justify-between py-1.5 border-b border-border/30">
                <span className="text-text-secondary font-medium flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 text-info" />
                  Email:
                </span>
                <span className="font-mono text-text-primary">
                  <a
                    href={`mailto:${client.contactEmail}`}
                    className="hover:underline text-primary-600"
                  >
                    {client.contactEmail}
                  </a>
                </span>
              </div>
              <div className="flex items-center justify-between py-1.5 border-b border-border/30">
                <span className="text-text-secondary font-medium flex items-center gap-1.5">
                  <FolderGit className="h-3.5 w-3.5 text-warning" />
                  Progetti totali:
                </span>
                <span className="font-semibold text-text-primary">
                  {projects.length}
                </span>
              </div>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-text-secondary font-medium flex items-center gap-1.5">
                  <Landmark className="h-3.5 w-3.5 text-success" />
                  Fatturato cumulativo:
                </span>
                <span className="font-bold text-text-primary">
                  {formatCurrency(totalRevenue || client.totalRevenue)}
                </span>
              </div>
            </div>
          </CardContent>
          <CardFooter className="pt-2 pb-4 flex justify-center bg-card-muted/20 border-t border-border/20">
            <Badge variant="success" className="px-4 py-1 text-xs">
              Cliente Attivo
            </Badge>
          </CardFooter>
        </Card>

        {/* Aggregate KPI Summary Cards */}
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="flex flex-col justify-between p-6">
            <div className="space-y-2">
              <span className="text-xs font-bold text-text-secondary uppercase tracking-wider">
                Budget Contrattualizzato
              </span>
              <h3 className="text-2xl font-extrabold text-text-primary">
                {formatCurrency(totalBudget)}
              </h3>
              <p className="text-[10px] text-text-secondary">
                Valore totale dei contratti attivi
              </p>
            </div>
            <div className="h-1 bg-primary-500 rounded-full w-full mt-4" />
          </Card>
          <Card className="flex flex-col justify-between p-6">
            <div className="space-y-2">
              <span className="text-xs font-bold text-text-secondary uppercase tracking-wider">
                Fatturato Incassato
              </span>
              <h3 className="text-2xl font-extrabold text-success">
                {formatCurrency(totalPaid)}
              </h3>
              <p className="text-[10px] text-text-secondary">
                {formatCurrency(totalInvoiced)} fatturati (
                {((totalPaid / (totalInvoiced || 1)) * 100).toFixed(0)}%)
              </p>
            </div>
            <div className="h-1 bg-success rounded-full w-full mt-4" />
          </Card>
          <Card className="flex flex-col justify-between p-6">
            <div className="space-y-2">
              <span className="text-xs font-bold text-text-secondary uppercase tracking-wider">
                Fatture Pendenti
              </span>
              <h3 className="text-2xl font-extrabold text-warning">
                {formatCurrency(totalOutstanding)}
              </h3>
              <p className="text-[10px] text-text-secondary">
                Fatture inviate in attesa di saldo
              </p>
            </div>
            <div className="h-1 bg-warning rounded-full w-full mt-4" />
          </Card>
        </div>
      </div>

      {/* Tabs Container */}
      <div className="space-y-4">
        {/* Tab selectors */}
        <div className="flex gap-2 border-b border-border/50 pb-2">
          <button
            onClick={() => setActiveTab("projects")}
            className={cn(
              "px-4 py-2 text-sm font-semibold border-b-2 -mb-2 transition-all cursor-pointer",
              activeTab === "projects"
                ? "border-primary-600 text-primary-600"
                : "border-transparent text-text-secondary hover:text-text-primary",
            )}
          >
            Progetti ({projects.length})
          </button>
          <button
            onClick={() => setActiveTab("financials")}
            className={cn(
              "px-4 py-2 text-sm font-semibold border-b-2 -mb-2 transition-all cursor-pointer",
              activeTab === "financials"
                ? "border-primary-600 text-primary-600"
                : "border-transparent text-text-secondary hover:text-text-primary",
            )}
          >
            Dati Finanziari & Fatture ({invoices.length})
          </button>
        </div>

        {/* Tab Contents */}
        {activeTab === "projects" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
            {projects.length === 0 ? (
              <div className="col-span-2 text-center py-12 bg-card rounded-xl border border-border/40 text-sm text-text-muted">
                Nessun progetto associato a questo cliente.
              </div>
            ) : (
              projects.map((proj) => (
                <Card
                  key={proj.id}
                  className="relative hover:border-primary-500/50 transition-all flex flex-col justify-between"
                >
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start gap-4">
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold text-text-muted uppercase tracking-wider">
                          Progetto Clienti
                        </span>
                        <CardTitle className="text-base font-bold leading-snug">
                          {proj.title}
                        </CardTitle>
                      </div>
                      {getProjectStatusBadge(proj.status)}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3 pb-3 text-xs">
                    <div className="flex items-center justify-between py-1 border-b border-border/20 text-text-secondary">
                      <span>Owner:</span>
                      <strong className="text-text-primary">
                        {proj.ownerName || "Delivery Team"}
                      </strong>
                    </div>
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <div>
                        <span className="text-text-muted block">Budget:</span>
                        <span className="font-semibold text-text-primary text-sm">
                          {formatCurrency(proj.budget)}
                        </span>
                      </div>
                      <div>
                        <span className="text-text-muted block">
                          Fatturato:
                        </span>
                        <span className="font-semibold text-text-primary text-sm">
                          {formatCurrency(proj.revenue)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="pt-2 pb-3 bg-card-muted/10 border-t border-border/20 text-[10px] text-text-muted">
                    <span>
                      Avviato il:{" "}
                      {new Date(proj.createdAt).toLocaleDateString("it-IT", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </CardFooter>
                </Card>
              ))
            )}
          </div>
        )}

        {activeTab === "financials" && (
          <div className="space-y-6 animate-fade-in">
            {/* Invoices list card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-bold">
                  Fatture Emesse
                </CardTitle>
                <CardDescription className="text-xs">
                  Registro contabile delle fatture associate al cliente.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm border-collapse">
                    <thead>
                      <tr className="bg-card-muted/50 border-b border-border/40 text-text-secondary text-xs uppercase tracking-wider font-semibold">
                        <th className="px-6 py-3">Fattura ID</th>
                        <th className="px-6 py-3">Importo</th>
                        <th className="px-6 py-3">Stato</th>
                        <th className="px-6 py-3">Scadenza</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/20">
                      {invoices.length === 0 ? (
                        <tr>
                          <td
                            colSpan={4}
                            className="text-center py-8 text-text-muted text-xs"
                          >
                            Nessuna fattura registrata per questo cliente.
                          </td>
                        </tr>
                      ) : (
                        invoices.map((inv) => (
                          <tr key={inv.id} className="hover:bg-card-muted/10">
                            <td className="px-6 py-4 font-semibold font-mono text-xs text-text-primary">
                              {inv.id}
                            </td>
                            <td className="px-6 py-4 font-bold text-text-primary">
                              {formatCurrency(inv.amount)}
                            </td>
                            <td className="px-6 py-4">
                              {getInvoiceStatusBadge(inv.status)}
                            </td>
                            <td className="px-6 py-4 text-xs text-text-secondary">
                              {inv.dueDate}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
